<template>
  <div>
    <slot v-bind:open="open" />

    <Modal :show="show" @closeModal="close" size="lg">
      <h1 class="mb-6 text-xl font-semibold text-gray-900">
        <template v-if="readonly">Dispatch Note Details</template>
        <template v-else>
          Create Dispatch Note
        </template>
      </h1>
      <div>
        <div class="grid grid-cols-2 gap-3">
          <div class="flex col-span-full justify-between">
            <div v-if="items.length > 0"  class="w-full max-w-lg">
              <OrderItemsSelect
                origin="top-left"
                :disabled="readonly"
              >
                <button class="btn-primary flex justify-between items-center">
                  <span>Order Items</span>
                  <Icon name="plus" class="h-5 w-5 ml-3" />
                </button>
                <div slot="dropdown" class="p-4">
                    <div v-if="items.length === 0">No Item available</div>
                    <div v-for="item in items"
                         :key="'item-option-'+item.id" class="p-2 flex items-center gap-x-2">
                        <div>
                          <input :id="item.id" type="checkbox" v-model="draft.items" :value="item" />
                        </div>
                        <label :for="item.id" class="text-xs cursor-pointer !capitalize" style="text-transform: capitalize;">
                          {{ item.description }}
                        </label>
                    </div>
                </div>
                <div v-if="items.length > 0" slot="footer" class="flex w-full justify-end gap-x-2">
                  <button class="btn-primary py-1.5 px-2 text-sm" @click="selectAll">
                    <template v-if="draft.items.length === 0">
                      Select All
                    </template>
                    <template v-else>Un-Select All</template>
                  </button>
                  <button class="btn-primary py-1.5 px-2 text-sm" @click="addSelected" :disabled="!draft.items.length">Add Selected</button>
                </div>
              </OrderItemsSelect>
            </div>
            <div v-else class="text-blue-500">
              All Items are Assigned
            </div>
          </div>
          <div class="space-x-4 py-4">
            <label>
              <input v-model="form.type" :disabled="readonly" name="type" type="radio" value="collection" /> For Collection
            </label>
            <label>
              <input v-model="form.type" :disabled="readonly" name="type" type="radio" value="delivery" /> For Delivery
            </label>
          </div>
        </div>

        <div v-if="form.type === 'delivery'" class="mt-3 space-y-3">
          <div class="flex gap-x-3">
            <div class="w-full max-w-lg">
              <SelectInput :disabled="readonly" label="Delivery Location" v-model="form.delivery_location">
                <option v-for="(value, key) in locations" :value="key">{{ value }}</option>
              </SelectInput>
            </div>
            <div v-if="form.delivery_location === 'island'" class="w-full max-w-xs">
              <SelectInput :disabled="readonly" label="Vessel Location" v-model="form.vessel_location">
                <option v-for="value in vesselLocations.island" :value="value">{{ value }}</option>
              </SelectInput>
              <div v-if="form.errors.vessel_location" class="form-error">{{ form.errors.vessel_location[0] }}</div>
            </div>
            <template v-if="readonly">
              {{ form.vessel }}
            </template>
            <template v-else>
              <div class="w-full" v-if="form.delivery_location === 'island'">
                <label class="form-label">Vessel</label>
                <div class="bg-white px-px">
                  <delivery-to-boat :disabled="readonly" @change="updateSelectedBoatID" :boatId="form.boat_id" :boat-name="form.vessel" :boat-location="form.vessel_location" :errors="form.errors.vessel"></delivery-to-boat>
                  <div v-if="form.errors.boat_id" class="form-error">{{ form.errors.boat_id[0] }}</div>
                </div>
              </div>
              <div v-else class="w-full">
                <label class="form-label mb-2">Delivery Address</label>
                <FormInput
                  type="text"
                  v-model="form.delivery_address"
                  :errors="$page.props.errors.delivery_address"
                />
              </div>
            </template>
          </div>
        </div>

        <div class="mt-3">
          <div class="flex items-end justify-between gap-x-3">
            <div class="flex gap-x-3">
              <DateInput :disabled="readonly" v-model="form.delivery_date" :label="form.type === 'collection' ? 'Handover Date' : 'Delivery Date'" @input="fetchTeams" />
              <template v-if="form.type === 'delivery'">
                <div>
                  <label class="form-label mb-2">Delivery Contact Number</label>
                  <FormInput
                    v-model="form.delivery_contact_number"
                    :errors="form.errors.delivery_contact_number"
                    :disabled="readonly"
                  />
                </div>
                <div>
                  <SelectInput
                    label="Delivery Team"
                    :disabled="readonly"
                    v-model="form.team_id"
                  >
                    <option v-for="team in teams" :key="'team-'+team.id" :value="team.id">
                      {{ team.name }} ({{ team.count }})
                    </option>
                  </SelectInput>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div class="flex flex-col mt-5">
          <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6">
            <div
              class="align-middle inline-block min-w-full overflow-hidden sm rounded-lg border border-gray-200"
              :class="{'border-red-500': form.errors.items}"
            >
              <table class="min-w-full">
                <thead>
                <tr>
                  <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    Item / Details
                  </th>
                  <th class="w-24 px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    UOM
                  </th>
                  <th class="w-48 px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    Qty
                  </th>
                  <th class="w-44 px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    Warehouse
                  </th>
                  <th class="w-14 bg-gray-50 px-2 py-3 border-b border-gray-200  text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider" />
                </tr>
                </thead>

                <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-if="form.items.length === 0">
                  <td class="py-2  text-sm text-center text-gray-500" colspan="5">no item(s)</td>
                </tr>
                <tr v-if="form.errors.items">
                  <td class="py-2  text-sm text-center form-error" colspan="6">{{ form.errors.items[0]}}</td>
                </tr>
                <tr v-if="form.errors.items">
                  <td class="py-2  text-sm text-center form-error" colspan="6">{{ form.errors.items[0]}}</td>
                </tr>
                <tr v-for="(item, index) in form.items" :key="'item-'+item.order_item_id">
                  <td class="py-1 px-2 font-medium text-gray-900">
                    <component is="label" :for="'item-'+item.id">
                      <div class="text-xs">{{ item.description }}</div>
                      <div class="text-xs text-gray-400">
                        {{ item.item_code }}
                      </div>
                    </component>
                  </td>
                  <td class="py-1 text-xs font-medium text-gray-900">
                    {{ item.unit }}
                  </td>
                  <td class="py-1 text-xs font-medium text-gray-900 flex gap-x-2 items-center">
                    <input
                      v-model.number="item.quantity_assigned"
                      class="w-20 form-input block text-xs sm:leading-5"
                      type="number"
                      :min="1"
                      :max="item.quantity_pending"
                      @input="e => updateQuantity(e, item)"
                    />
                    <div class="w-16 flex pr-2 items-center whitespace-nowrap">
                      <span class="mr-1">/ {{ item.quantity_pending }}</span>
                      <available-qty-badge
                        v-if="available_qty.loaded"
                        :item="available_qty.items[item.item_code]"
                        :unit="item.unit"
                        :qty="item.quantity_pending"
                      />
                    </div>
                  </td>
                  <td class="py-1 text-xs font-medium text-gray-900">
                    <SelectInput
                      v-model="item.warehouse_id"
                      @input="fetchAvailableQty(item)"
                    >
                      <option v-for="(warehouse, windx) in getWarehouseListFor(item.item_code)" :value="warehouse.id" :key="windx">{{ warehouse.name }}</option>
                    </SelectInput>
                  </td>
                  <td class="px-3">
                    <button
                      :disabled="readonly"
                      @click="removeItem(item)"
                      class="btn-red px-2 py-1.5">x</button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
      <div class="mt-4 flex gap-x-3 justify-end">
        <button type="button" class="btn-gray" @click="close">Close</button>
        <button
          v-if="isEdit && canVoid"
          type="button"
          class="btn-red"
          @click="voidNote"
        >
          Void
        </button>
        <button
          v-if="canVoid && (note && note.status === 'void')"
          type="button"
          class="btn-red"
          @click="deleteNote"
        >
          Delete
        </button>

        <LoadingButton
          v-if="!readonly && canCreate"
          :loading="form.processing"
          :disabled="form.processing && !form.items.length"
          class="btn-primary"
          @click="addRecord"
        >
          Create
        </LoadingButton>
      </div>
    </Modal>
  </div>
</template>

<script>
import SelectInput from "@/Shared/SelectInput.vue";
import FormInput from "@/Shared/Form/FormInput.vue";
import LoadingButton from "@/Shared/LoadingButton.vue";
import {mapFormValue} from "@/Utils";
import AvailableQtyBadge from "@/Shared/AvailableQtyBadge.vue";
import Icon from "@/Shared/Icon.vue";
import DeliveryToBoat from "../../DeliveryToBoat.vue";
import Modal from '@/Shared/Modal.vue'
import TeamSelectModal from "./TeamSelectModal.vue";
import SplitDeliveryNoteModal from "./SplitDeliveryNoteModal.vue";
import DateInput from "../../../../../Shared/DateInput.vue";
import OrderItemsSelect from "./OrderItemsSelect.vue";
import Badge from "../../../../../Shared/Badge.vue";
import {debounce} from "lodash";

export default {
  name: 'CreateDeliveryNoteModal',
  components: {
    Badge,
    OrderItemsSelect,
    DateInput,
    SplitDeliveryNoteModal,
    TeamSelectModal, DeliveryToBoat, Modal, Icon, AvailableQtyBadge, LoadingButton, FormInput, SelectInput},

  props: {
    order: Object,
    isLocked: Boolean,
    locations: Object,
    vesselLocations: Object,
    warehouseList: Array,
    note: null,
    getWarehouseListFor: Function,
  },

  data() {
    return {
      draft: {
        items: [],
      },
      available_qty: {
        loaded: false,
        items: {}
      },
      show: false,
      readonly: false,
      teams: [],
      form: this.$inertia.form({
        order_id: null,
        type: 'delivery',
        delivery_date: null,
        delivery_location: null,
        vessel: null,
        vessel_location: null,
        boat_id: null,
        delivery_contact_number: null,
        remarks: null,
        delivery_address: null,
        customer_account_id: null,
        delivery_teams: [],
        team_id: null,
        items: []
      })
    }
  },

  computed: {
    items() {
      if (Array.isArray(this.order.items) && this.order.items.length) {
        return this.order.items.filter(itm => !this.isItemInForm(itm.id) && itm.quantity_pending > 0)
      }
      return []
    },

    warehouses() {
      const wareHouseIds = new Set(this.order.items.map(itm => itm.warehouse_id))
      return this.warehouseList.filter(item => wareHouseIds.has(item.id))
    },

    isEdit() {
      return this.note && Object.keys(this.note).length && this.show === true && this.note.status === 'pending'
    },

    addedItemIds() {
      return this.form.items.map(item => item.order_item_id);
    },
    canCreate() {
      return this.addedItemIds.length && this.canAdmin('create-dispatch-notes');
    },
    canEdit() {
      return this.canAdmin('update-dispatch-notes');
    },
    canVoid() {
      return this.canAdmin('void-dispatch-notes');
    },
    deliveryTeams() {
      return teams => {
        if (Array.isArray(teams) && teams.length) {
          return teams.map(team => team.name).join(', ')
        }
        return 'N/A'
      }
    }
  },

  methods: {
    addSelected() {
      const items = this.draft.items.map(item => ({
        order_item_id: item.id,
        quantity_assigned: item.quantity_pending,
        quantity_pending: item.quantity_pending,
        unit: item.unit,
        item_code: item.item_code,
        description: item.description,
        warehouse_id: item.warehouse_id,
      }))
      this.form.items = [...this.form.items, ...items]

      this.$nextTick(() => {
        this.draft.items = []
        this.checkAvailability()
      })
    },
    selectAll() {
      if (Array.isArray(this.draft.items) && this.draft.items.length) {
        this.draft.items = []
      } else {
        this.draft.items = [...this.items]
      }
    },
    fetchTeams() {
      this.$http.get(this.route('ajax.delivery-teams.assigned-count', { date: this.form.delivery_date })).then(response => {
        this.teams = response.data
        this.form.delivery_teams = this.form.delivery_teams.map((deliveryTeam) => {
          const matchingTeam = this.teams.find((team) => team.id === deliveryTeam.id);
          if (matchingTeam) {
            return matchingTeam
          }
          return deliveryTeam;
        });

      })
    },
    assignTeam(team) {
      this.form.delivery_teams = [team];
    },
    open() {
      if (this.note && Object.keys(this.note).length) {
        this.readonly = true
        mapFormValue(this.form, this.note)

        this.checkAvailability()
      } else {
        this.form.order_id = this.order.id
        this.form.delivery_date = this.order.customer_handover_date
        this.form.delivery_location = this.order.delivery_location
        this.form.vessel = this.order.vessel
        this.form.vessel_location = this.order.vessel_location
        this.form.delivery_contact_number = this.order.delivery_contact_number
        this.form.remarks = this.order.remarks
        this.form.delivery_address = this.order.delivery_address
        this.form.boat_id = this.order.boat_id
        this.form.customer_account_id = this.order.customer_account_id
        if (this.order.customer_to_collect) {
          this.form.type = 'collection'
        }
      }
      this.fetchTeams()
      this.show = true
    },

    close() {
      this.form.reset()
      this.form.clearErrors()
      this.readonly = false
      this.show = false
    },

    checkAvailability() {
      if (Array.isArray(this.form.items) && this.form.items.length) {
        this.form.items.forEach((item)=> {
          this.available_qty.items = {}
          this.fetchAvailableQty(item)
        })
      }
    },

    removeItem(item) {
      const index = this.form.items.findIndex(itm => itm.id === item.id)
      this.form.items.splice(index, 1)
    },

    voidNote() {
      if (this.isEdit && confirm('Void this delivery note?')) {
        this.form.delete(this.route('delivery-notes.void', {
          note: this.note.id,
          order: this.note.order_id,
        }))
      }
    },

    deleteNote() {
      if (this.note && this.note.status === 'void' && confirm('Delete this delivery note?')) {
        this.form.delete(this.route('delivery-notes.void', {
          note: this.note.id,
          order: this.note.order_id,
          force: true,
        }))
      }
    },

    updateQuantity(e, item) {
      let value = e.target.value
      let quantity = parseInt(value, 10);
      if (quantity > item.quantity_pending) {
        quantity = item.quantity_pending
      }

      const itemIndex = this.form.items.findIndex(itm => itm.order_item_id === item.order_item_id);

      this.form.items[itemIndex].quantity_assigned = quantity;
    },

    isItemInForm(itemId) {
      return this.addedItemIds.includes(itemId)
    },

    addRecord() {
      if (this.readonly && !this.canCreate) {
        return;
      }

      this.form.post(this.route('delivery-notes.store'), {
        preserveState: 'errors',
        onSuccess: () => this.close(),
      })
    },

    fetchAvailableQty(item) {
      this.available_qty.loaded = false;
      this.$http.get(this.route('ajax.order_item.quantity',{
        order_id:this.order.id,
        item_code:item.item_code,
        unit: item.unit,
        warehouse_id: item.warehouse_id
      })).then((response) => {

        let existing = this.available_qty.items[response.data.item_code] || {}
        this.available_qty.items = {
          ...this.available_qty.items,
          [response.data.item_code] : {
            ...existing,
            [response.data.unit] : response.data,
          }
        };
        this.available_qty.loaded = true;
      })
    },

    updateSelectedBoatID(boat) {
      this.form.boat_id = boat.id;
      this.form.vessel = boat.name;
    }
  },

  watch: {
    form: {
      handler(v) {
        if (v.type === 'collection') {
          this.form.reset('boat_id', 'vessel', 'vessel_location', 'delivery_address')
        } else {
          if (v.delivery_location === 'island') {
            this.form.reset('delivery_address')
          } else {
            this.form.reset('boat_id', 'vessel', 'vessel_location')
          }
        }
      },
      deep: true,
    }
  }
}
</script>
